<template>
  <component is="style" type="text/css">
    {{ model.state.elementStyling?.generalStyling }}
  </component>
  <div v-if="model.state.gameValid" class="product-selector">
    <div v-if="model.state.images && model.state.images?.length > 0" class="product-selector__wrapper">
      <Swiper
        :modules="modules"
        :slides-per-view="model.state.sliderOptions?.slidesPerView"
        :slides-per-column="model.state.sliderOptions?.slidesPerColumn"
        :slides-per-column-fill="'row'"
        :grid="{ rows: model.state.sliderOptions?.slidesPerColumn, fill: 'row' }"
        :pagination="
          model.state.sliderOptions?.pagination
            ? {
                el: '.swiper-pagination',
                clickable: true
              }
            : false
        "
        :loop="!!model.state.sliderOptions?.loop"
        :centered-slides="model.state.sliderOptions?.centeredSlides"
        :space-between="model.state.sliderOptions?.spaceBetween"
      >
        <SwiperSlide
          v-for="image in model.state.images"
          :key="image.id"
          class="product-selector__image"
          :class="{ 'product-selector__image--selected': selectedImage === image.id }"
          @click="onSelectImage(image.id)"
        >
          <div
            class="product-selector__image-item"
            :class="{ 'product-selector__image-item--alfa': model.state.general?.activeState === 'alfa' }"
          >
            <img class="product-selector__image-img" :src="image?.image" alt="" />
          </div>
          <h5 v-if="image.label !== ''" class="product-selector__image-label">
            {{ image.label }}
          </h5>
        </SwiperSlide>
        <div class="swiper-pagination"></div>
      </Swiper>
    </div>

    <div v-if="model.state.general?.showAnswerButton" class="product-selector__button-wrapper">
      <button
        type="button"
        class="product-selector__button content__item-action button btn"
        :class="{ 'button--disabled': isError }"
        @click="onAnswer"
      >
        <span class="product-selector__button-disabled-text">
          {{ model.state.general?.selectAnswerBtn }}
        </span>

        <span class="product-selector__button-enabled-text">
          {{ model.state.general?.answerBtn }}
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { PropType } from 'vue';
import { defineComponent, ref } from 'vue';
import SwiperCore, { Grid, Navigation, Pagination } from 'swiper';
import type { GameProductSelectorModel } from '@/src/components/games/product-selector/Model';

import 'swiper/css';
import 'swiper/css/lazy';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/grid';

import useEndGame from '@/src/hooks/useEndGame';
import { playAudio } from '@/src/utilities/AudioPlayer';
import type { MetricData } from '@/src/store/campaign';
import { useCampaignStore } from '@/src/store/campaign';

// install Swiper modules
SwiperCore.use([Navigation, Pagination, Grid]);

interface Settings {
  replacementTags: MetricData;
  metrics: MetricData;
}

export default defineComponent({
  name: 'Game-Shell',
  components: {
    Swiper,
    SwiperSlide
  },
  props: {
    model: {
      type: Object as PropType<GameProductSelectorModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();
    const isError = ref(false);
    const selectedImage = ref<string | null>(null);

    let gameOver = false;

    const onSelectImage = (imageId: string) => {
      if (props.model.state.advanced.sound.enabled && props.model.state.advanced.sound.src) {
        playAudio(props.model.state.advanced.sound.src);
      }

      selectedImage.value = imageId;
      if (!props.model.state.general.showAnswerButton) {
        onAnswer();
      }
    };

    const onAnswer = () => {
      if (gameOver) return;

      if (!selectedImage.value || selectedImage.value === '') {
        const RESET_ERROR_AFTER_MS = 1200;
        isError.value = true;

        setTimeout(() => {
          isError.value = false;
        }, RESET_ERROR_AFTER_MS);
      } else {
        gameOver = true;

        const settings: Settings = {
          replacementTags: {},
          metrics: {}
        };

        if (props.model.state.images && props.model.state.images.length > 0) {
          props.model.state.images.forEach((image) => {
            if (selectedImage.value) {
              if (image.id && parseInt(image.id, 10) === parseInt(selectedImage.value, 10)) {
                settings.replacementTags.image_answer = '<img class="answer-image" src="' + image.image + '" />';
                settings.replacementTags.label_answer = '<span class="answer-label">' + image.label + '</span>';
              }
            }
          });
        }

        settings.metrics.product = selectedImage.value;

        campaignStore.metricData = settings.metrics;
        campaignStore.addReplacementTags(settings.replacementTags);

        useEndGame(true);
      }
    };

    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    if (readyPromiseResolve) {
      readyPromiseResolve();
    }

    return {
      selectedImage,
      isError,
      onAnswer,
      onSelectImage,
      modules: [Navigation, Pagination],
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>

<style lang="scss">
.swiper-container {
  width: 100%;
  height: auto;
  margin-left: auto;
  margin-right: auto;
}

.swiper-slide {
  text-align: center;
  font-size: 1.8rem;
}

.swiper-pagination {
  position: relative !important;
  bottom: 0 !important;
  padding: 4rem 0 2rem;
}

.swiper-container-multirow > .swiper-wrapper {
  flex-direction: row !important;
  align-items: end;
  align-content: start;
}

.swiper-pagination-bullet {
  background-color: #fff;
  opacity: 0.5;
}

.product-selector__wrapper {
  padding-top: 3rem;
}

.product-selector .product-selector__image {
  cursor: pointer;
  box-sizing: border-box;
  height: auto;
}

.product-selector .product-selector__image-item--alfa .product-selector__image-img {
  opacity: 0.5;
}

.product-selector .product-selector__image-item--alfa .product-selector__image-img:active,
.touch .product-selector .product-selector__image-item--alfa .product-selector__image-img:active,
.no-touch .product-selector .product-selector__image-item--alfa .product-selector__image-img:hover,
.product-selector .product-selector__image--selected .product-selector__image-item--alfa .product-selector__image-img {
  transition: opacity 150ms linear;
  opacity: 1;
}

.product-selector .product-selector__image-img {
  display: inline-block;
  margin-bottom: 1.5rem;
}

.product-selector .product-selector__image-label {
  padding: 0 1rem 1.5rem;
}

.product-selector .product-selector__button-wrapper {
  text-align: center;
}

.product-selector .product-selector__button-wrapper {
  position: relative;
  overflow: hidden;
  margin-top: 3rem;
}

.product-selector .product-selector__button-wrapper .button {
  margin: 0;
  min-width: 17rem;
}

.product-selector .product-selector__button-wrapper .button::before {
  content: ' ';
  display: none !important;
}

.product-selector .product-selector__button-wrapper .button.button--disabled {
  cursor: default;
}

.product-selector .product-selector__button-wrapper .button .product-selector__button-enabled-text,
.product-selector .product-selector__button-wrapper .button .product-selector__button-disabled-text {
  display: block;
  padding: 1.7rem;
  transition: transform 0.3s ease-out, opacity 0.3s linear;
}

.product-selector .product-selector__button-wrapper .button .product-selector__button-enabled-text {
  transform: translate(0, 0%);
  opacity: 1;
  position: relative;
  padding: 0 !important;
}

.product-selector .product-selector__button-wrapper .button .product-selector__button-disabled-text {
  transform: translate(0, -100%);
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.product-selector .product-selector__button-wrapper .button.button--disabled .product-selector__button-enabled-text {
  transform: translate(0, 4.5rem);
  opacity: 0;
}

.product-selector .product-selector__button-wrapper .button.button--disabled .product-selector__button-disabled-text {
  transform: translate(0, 0%);
  opacity: 1;
}
</style>
